import { BarChart, Bar, XAxis, YAxis, LabelList } from "recharts";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { border, body } from "./tokens";
import { FONT_BODY, FONT_DISPLAY, TEXT_PRIMARY, TEXT_SECONDARY } from "./BrandChart";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';

const GAP_CLOSURES_CHART_HEIGHT = 400;
const GAP_CLOSURES_PRINT_CHART_HEIGHT = 240;

const gapClosuresBarChartLayout = (printStatic: boolean) =>
  printStatic
    ? {
        margin: { top: 18, right: 4, left: 0, bottom: 8 },
        barGap: 2,
        barCategoryGap: '4%',
        barSize: 24,
        tickFontSize: 12,
        labelFontSize: 12,
      }
    : {
        margin: { top: 24, right: 8, bottom: 24, left: 8 },
        barGap: 2,
        barCategoryGap: '18%',
        barSize: 32,
        tickFontSize: 13,
        labelFontSize: 14,
      };

const STATUS_CONFIG = [
  { key: "inProgress", label: "In Progress", color: "var(--reema-midnight-100)" },
  { key: "scheduled", label: "Scheduled", color: "#154A74" },
  { key: "completed", label: "Completed", color: "#3E7A6B" },
  { key: "refused", label: "Refused", color: "var(--reema-coral-100)" },
] as const;

const DEFAULT_DATA = [
  { group: "A1C", inProgress: 62, scheduled: 15, completed: 34, refused: 7 },
  { group: "Dental", inProgress: 45, scheduled: 8, completed: 22, refused: 3 },
  { group: "Breast", inProgress: 31, scheduled: 1, completed: 11, refused: 1 },
  { group: "Colon", inProgress: 90, scheduled: 21, completed: 28, refused: 5 },
  { group: "Kidney", inProgress: 28, scheduled: 5, completed: 19, refused: 2 },
];

const readChartData = (data: Record<string, unknown>) => {
  if (!Array.isArray(data.matrix)) {
    return DEFAULT_DATA;
  }

  return (data.matrix as Array<{ screening: string; values: number[] }>).map(
    row => ({
      group: row.screening,
      inProgress: row.values[0] ?? 0,
      scheduled: row.values[1] ?? 0,
      completed: row.values[2] ?? 0,
      refused: row.values[3] ?? 0,
    }),
  );
};

const GapClosures = ({ data }: ReportHostedSectionProps) => {
  const chartData = readChartData(data);
  const printStatic = useReportsPrintStaticLayout();
  const chartHeight = printStatic ? GAP_CLOSURES_PRINT_CHART_HEIGHT : GAP_CLOSURES_CHART_HEIGHT;
  const chartLayout = gapClosuresBarChartLayout(printStatic);
  const { containerRef: gapClosuresChartRef, dimensions: gapClosuresChartDimensions } =
    useReportChartDimensions(chartHeight);

  return (
    <HostedReportSectionLayout
      sectionDomId="gap-closures"
      className="section--white report-hosted-section-body"
      style={{ background: "var(--white)", fontFamily: body }}
      banner={
        <SectionBanner title="Gap Closures" iconHref="#icon_relationship_engine" />
      }
    >
      <SectionReveal delay={0.1}>
        <div
          className="gap-closures-card rounded-2xl p-6 md:p-8 mb-10"
          style={{ background: "#fff", border: `1px solid ${border}` }}
        >
          <div
            className="gap-closures-legend flex flex-wrap items-center gap-x-6 gap-y-2 mb-6"
            style={{ fontFamily: FONT_BODY, fontSize: 13, color: TEXT_SECONDARY }}
          >
            {STATUS_CONFIG.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: s.color,
                  }}
                />
                <span style={{ fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </div>

          <div
            ref={gapClosuresChartRef}
            className="gap-closures-chart report-chart-host"
            style={{ width: "100%", minWidth: 0, height: chartHeight }}
          >
            {gapClosuresChartDimensions ? (
              <BarChart
                width={gapClosuresChartDimensions.width}
                height={gapClosuresChartDimensions.height}
                data={chartData}
                margin={chartLayout.margin}
                barGap={chartLayout.barGap}
                barCategoryGap={chartLayout.barCategoryGap}
              >
                <YAxis type="number" hide />
                <XAxis
                  dataKey="group"
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 0, right: 0 }}
                  tick={{
                    fontFamily: FONT_BODY,
                    fontSize: chartLayout.tickFontSize,
                    fontWeight: 500,
                    fill: TEXT_SECONDARY,
                  }}
                  interval={0}
                />
                {STATUS_CONFIG.map((s) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    fill={s.color}
                    radius={[4, 4, 0, 0]}
                    barSize={chartLayout.barSize}
                  >
                    <LabelList
                      dataKey={s.key}
                      position="top"
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 500,
                        fontSize: chartLayout.labelFontSize,
                        fill: TEXT_PRIMARY,
                      }}
                    />
                  </Bar>
                ))}
              </BarChart>
            ) : null}
          </div>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default GapClosures;
